import { useState } from 'react';

import type { LeaseActionRestrictionDto } from '@zg-rentals/ts-leasing-client';
import {
  AssistChip,
  Box,
  Card,
  Divider,
  Heading,
  Paragraph,
  Tag,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconCalendarFilled, IconDownloadFilled, IconZillowMark } from '@zillow/constellation-icons';

import type Lease from '../../models/Lease';
import type { TagTone } from '../../models/Lease';
import type { Property } from '../../models';
import { LeaseAction } from '../../models';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import LastMessage from '../../components/Widgets/LastMessage';
import WhoHasSigned from '../../components/Widgets/WhoHasSigned';
import { FULLSTORY_EXCLUDE } from '../../constants/analytics';
import { LeaseTypes } from '../../models/Types';
import { differenceInDaysBetweenDateAndToday } from '../../constants/functionConstants';
import {
  DOWNLOAD_TOAST_MESSAGES,
  LEASE_EXTENSION_DAYS_ALLOWED_BEFORE_END_DATE,
  ZILLOW_LEASES_HELP,
} from '../../constants/stringConstants';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { downloadFileFromUrl } from '../../utils/urlUtils';
import { baseUrl } from '../../utils/apiConstants';
import { logError } from '@zg-rentals/log-error';

type Props = {
  lease: Lease;
  property: Property;
  index: number;
  leaseActions: Array<LeaseAction>;
};

function LeaseRow({ lease, property, index, leaseActions }: Props): JSX.Element | null {
  const latestExtension = lease?.latestExtension ?? null;
  const { enqueueToast } = useToast();
  const [downloadingLeaseHistory, setDownloadingLeaseHistory] = useState<string | null>(null);

  function renderLeaseExtras(): JSX.Element | null {
    const { id, messageData } = lease;

    return (
      (lease.showLastMessage() || lease.showWhoHasSigned()) && (
        <Box css={{ marginTop: 'tighter' }}>
          {lease.showLastMessage() && <LastMessage leaseId={id} passedInMessageObject={messageData} />}
          {lease.showWhoHasSigned() && (
            <Box css={{ marginTop: 'tight' }}>
              <WhoHasSigned leaseId={id} isInitiallyExpanded />
            </Box>
          )}
        </Box>
      )
    );
  }

  function renderLeaseExtensionExtras(): JSX.Element | null {
    return (
      <>
        {latestExtension?.messageData && (
          <Box css={{ marginTop: 'tight' }}>
            <LastMessage leaseId={lease.id} passedInMessageObject={latestExtension.messageData} />
          </Box>
        )}
        {latestExtension?.showWhoHasSigned() && (
          <Box css={{ marginTop: 'tight' }}>
            <WhoHasSigned isInitiallyExpanded leaseExtensionId={latestExtension.id} />
          </Box>
        )}
      </>
    );
  }

  function renderExpirationTag(numberOfDaysTillExpiration: number) {
    return (
      <Tag tone="critical" css={{ textTransform: 'capitalize' }}>
        {numberOfDaysTillExpiration === 1 ? 'Expires in 1 day' : `Expires in ${numberOfDaysTillExpiration} days`}
      </Tag>
    );
  }

  const onFileDownloadedSuccessToast = () => {
    enqueueToast(<Toast tone="info">{DOWNLOAD_TOAST_MESSAGES.LEASE_SUCCESS}</Toast>);
  };

  const onFileDownloadedErrorToast = () => {
    enqueueToast(
      <Toast
        tone="info"
        actionButton={<TextButton onClick={() => window.open(ZILLOW_LEASES_HELP, '_blank')}>Get lease help</TextButton>}
      >
        {DOWNLOAD_TOAST_MESSAGES.LEASE_ERROR}
      </Toast>,
    );
  };

  const handleLeaseHistoryDownload = async (action: string) => {
    if (downloadingLeaseHistory === action) return; // Prevent multiple clicks

    setDownloadingLeaseHistory(action);
    try {
      await downloadFileFromUrl(`${baseUrl}${action}`);
      onFileDownloadedSuccessToast();
    } catch (err) {
      logError({ error: err, context: 'LeaseRow - lease history download failed' });
      onFileDownloadedErrorToast();
    } finally {
      setDownloadingLeaseHistory(null);
    }
  };

  function renderLeaseHistory() {
    return lease?.leaseHistory?.length > 0 ? (
      <Box css={{ display: 'flex', gap: 'tighter', flexWrap: 'wrap' }}>
        {lease.leaseHistory.map((leaseHistoryItem) => (
          <AssistChip
            key={leaseHistoryItem.label}
            icon={<IconDownloadFilled />}
            onClick={() => handleLeaseHistoryDownload(leaseHistoryItem.action)}
            loading={downloadingLeaseHistory === leaseHistoryItem.action}
          >
            {leaseHistoryItem.label}
          </AssistChip>
        ))}
      </Box>
    ) : null;
  }

  function renderLeaseDetails(index: number): JSX.Element | null {
    const differenceInDaysBetweenLeaseEndDateAndToday = lease?.endDate
      ? differenceInDaysBetweenDateAndToday(lease.endDate)
      : null;

    return (
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { base: 'row', mdDown: 'column' },
          gap: 'tighter',
        }}
      >
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          {latestExtension ? (
            <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }} className={FULLSTORY_EXCLUDE}>
              Original lease
            </Heading>
          ) : (
            <Heading level={3} css={{ textStyle: 'responsive.heading-xs' }} className={FULLSTORY_EXCLUDE}>
              {lease.getLabel(`Lease #${index + 1}`)}
            </Heading>
          )}
          {lease.startDate && (
            <Paragraph>
              {lease.getFriendlyStartDate()}
              {'–'}
              {lease.endDate ? lease.getFriendlyEndDate() : 'no end date'}
            </Paragraph>
          )}
          <Box css={{ display: 'flex', gap: 'tighter', flexWrap: 'wrap' }}>
            {differenceInDaysBetweenLeaseEndDateAndToday &&
            differenceInDaysBetweenLeaseEndDateAndToday <= LEASE_EXTENSION_DAYS_ALLOWED_BEFORE_END_DATE &&
            differenceInDaysBetweenLeaseEndDateAndToday > 0 &&
            lease.status === LeaseStatus.ACTIVE ? (
              renderExpirationTag(differenceInDaysBetweenLeaseEndDateAndToday)
            ) : (
              <Tag tone={lease.getBadgeColor() as TagTone} css={{ textTransform: 'capitalize' }}>
                {lease.getHumanReadableStatus()}
              </Tag>
            )}
            {(lease.leaseType === LeaseTypes.ZILLOW_WIZARD || lease.leaseType === LeaseTypes.COSMOS) && (
              <Tag tone="blue" icon={<IconZillowMark />} css={{ textTransform: 'capitalize' }}>
                Zillow lease
              </Tag>
            )}
          </Box>
          {lease.recentActivity && <Paragraph css={{ textStyle: 'body-sm' }}>{lease.recentActivity}</Paragraph>}
          {renderLeaseHistory()}
        </Box>
        <LeaseActionButtonGroup className="lease-dashboard-actions" fluid {...{ leaseActions, lease, property }} />
      </Box>
    );
  }

  function renderLeaseExtensionDetails(): JSX.Element | null {
    const extensionActions = latestExtension
      ? latestExtension?.actions?.map(
          (action) =>
            new LeaseAction(
              action.name,
              action.disabled,
              (action as { restriction?: LeaseActionRestrictionDto }).restriction,
            ),
        )
      : null;
    const differenceInDaysBetweenLeaseExtensionEndDateAndToday = latestExtension?.endDate
      ? differenceInDaysBetweenDateAndToday(new Date(latestExtension.endDate))
      : null;

    return (
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { base: 'row', mdDown: 'column' },
          gap: 'tighter',
        }}
      >
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <Heading level={3} css={{ textStyle: 'responsive.heading-xs' }} className={FULLSTORY_EXCLUDE}>
            {lease.getLabel(`Lease #${index + 1}`)}
          </Heading>
          {latestExtension.startDate && (
            <Paragraph>
              {latestExtension.getFriendlyStartDate()}
              {'–'}
              {latestExtension.endDate ? latestExtension.getFriendlyEndDate() : 'no end date'}
            </Paragraph>
          )}
          <Box css={{ display: 'flex', gap: 'tighter', flexWrap: 'wrap' }}>
            {differenceInDaysBetweenLeaseExtensionEndDateAndToday &&
            differenceInDaysBetweenLeaseExtensionEndDateAndToday <= LEASE_EXTENSION_DAYS_ALLOWED_BEFORE_END_DATE &&
            differenceInDaysBetweenLeaseExtensionEndDateAndToday > 0 &&
            latestExtension.status === LeaseStatus.ACTIVE ? (
              renderExpirationTag(differenceInDaysBetweenLeaseExtensionEndDateAndToday)
            ) : (
              <Tag tone={latestExtension.getBadgeColor() as TagTone} css={{ textTransform: 'capitalize' }}>
                {latestExtension.getHumanReadableStatus()}
              </Tag>
            )}

            <Tag tone="blue" icon={<IconCalendarFilled />} css={{ textTransform: 'capitalize' }}>
              Lease renewal
            </Tag>
          </Box>
          {latestExtension.recentActivity && (
            <Paragraph css={{ textStyle: 'body-sm' }}>{latestExtension.recentActivity}</Paragraph>
          )}
        </Box>
        <LeaseActionButtonGroup fluid leaseActions={extensionActions} lease={lease} property={property} />
      </Box>
    );
  }

  return (
    <Card elevated={false} tone="neutral" css={{ padding: 'loose' }}>
      {latestExtension && (
        <>
          {renderLeaseExtensionDetails()}
          {renderLeaseExtensionExtras()}
          <Divider css={{ marginTop: 'tight', marginBottom: 'tight' }} />
        </>
      )}
      {renderLeaseDetails(index)}
      {renderLeaseExtras()}
    </Card>
  );
}

export default LeaseRow;
